import { getDataGridToolbarProps } from 'features/shared/utility/dataGridToolbarHelper';
import usePermissions from 'features/shared/hooks/usePermissions';
import { PERMISSION_RESOURCES } from 'features/shared/constants/permissionResources';
import FormSpinner from 'components/FormSpinner';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import { CommonDataGrid } from 'features/shared/components/table';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import { RMSButton } from '../../../shared/components/buttons';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import { ASSOCIATE_GRID_COLUMNS } from '../../constants/personAssociateFormConstants';
import PersonAssociateDialog from './PersonAssociateDialog';
import { getPeopleAssociates, deletePersonAssociate } from '../../services/userService';

const PersonAssociatesTab = ({ personId }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [associates, setAssociates] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingAssociate, setEditingAssociate] = useState(null);
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();
  const canEditPerson = usePermissions(PERMISSION_RESOURCES.PERSON).Edit;

  const loadAssociates = useCallback(async () => {
    if (!personId) return;

    await executeApiAction({
      setLoading,
      dispatch,
      action: () => getPeopleAssociates(personId),
      onSuccess: (data) => setAssociates(data),
    });
  }, [dispatch, personId]);

  useEffect(() => {
    loadAssociates();
  }, [loadAssociates]);

  const handleAdd = useCallback(() => {
    setEditingAssociate(null);
    setFormOpen(true);
  }, []);

  const handleEdit = useCallback((row) => {
    setEditingAssociate(row);
    setFormOpen(true);
  }, []);

  const handleFormClose = useCallback(() => {
    setFormOpen(false);
    setEditingAssociate(null);
  }, []);

  const handleSaved = useCallback(() => {
    handleFormClose();
    loadAssociates();
  }, [handleFormClose, loadAssociates]);

  const handleDelete = useCallback(
    (row) => {
      requestConfirm(
        `Are you sure you want to permanently delete this "${row.FullName || 'associate'}" record?`,
        async () => {
          await executeApiAction({
            setLoading,
            dispatch,
            action: () =>
              deletePersonAssociate({
                ptsPersonID: personId,
                ptsAssociateID: row.ptsAssociateID,
              }),
            onSuccess: () => {
              dispatch(notify('Associate deleted successfully', 'success'));
              loadAssociates();
            },
          });
        },
        'Delete Associate'
      );
    },
    [requestConfirm, dispatch, personId, loadAssociates]
  );

  return (
    <Box className="person-names-tab__grid-wrap">
      {loading && <FormSpinner />}
      <Box className="person-addresses-tab__toolbar">
        <RMSButton
          label="Add Associate"
          prefixIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={!canEditPerson}
        />
      </Box>

      <CommonDataGrid
        rows={associates}
        columns={ASSOCIATE_GRID_COLUMNS}
        getRowId={(row) => row.ptsAssociateID}
        toolbar={getDataGridToolbarProps({ printTitle: 'Person Associates' })}
        actions={{
          edit: { visible: true, onClick: handleEdit, disabled: !canEditPerson },
          delete: { visible: true, onClick: handleDelete, disabled: !canEditPerson },
        }}
      />

      {formOpen && (
        <PersonAssociateDialog
          open={formOpen}
          personId={personId}
          associate={editingAssociate}
          onClose={handleFormClose}
          onSaved={handleSaved}
        />
      )}

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </Box>
  );
};

export default PersonAssociatesTab;
